import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { chProjectService } from '../services';
import { useModal } from '../../contexts/ModalContext';
import { PLATFORM_LABELS, type ChFollowers, type ChPostPlatform } from '../types';

interface Props {
  projectId: string;
  followers: ChFollowers | null | undefined;
  onSaved: (followers: ChFollowers) => void;
  onClose: () => void;
}

/** Followers per platform for the project's overview and client report. */
export function FollowersEditor({ projectId, followers, onSaved, onClose }: Props) {
  const { toast } = useModal();
  const [draft, setDraft] = useState<Partial<Record<ChPostPlatform, string>>>(() =>
    Object.fromEntries(Object.entries(followers || {}).map(([k, v]) => [k, String(v)]))
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    const clean: ChFollowers = {};
    for (const [platform, value] of Object.entries(draft) as [ChPostPlatform, string][]) {
      const digits = (value || '').replace(/[\s.,]/g, '');
      if (/^\d+$/.test(digits)) clean[platform] = Number(digits);
    }
    setSaving(true);
    try {
      onSaved(await chProjectService.setFollowers(projectId, clean));
      toast.success('Seguidores atualizados');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível guardar os seguidores', 8000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ch-card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[var(--ch-ink)]">Seguidores da conta</h3>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100" aria-label="Fechar">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(Object.entries(PLATFORM_LABELS) as [ChPostPlatform, string][]).map(([platform, label]) => (
          <label key={platform} className="block">
            <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
            <input
              type="text"
              inputMode="numeric"
              className="ch-input h-9 text-sm tabular-nums mt-0.5"
              placeholder="—"
              value={draft[platform] ?? ''}
              onChange={e => setDraft(d => ({ ...d, [platform]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">Valor atual de cada perfil. Aparece também no link do cliente.</p>
        <button onClick={handleSave} disabled={saving} className="ch-btn ch-btn-primary ch-btn-sm flex-shrink-0">
          <Save className="w-3.5 h-3.5" />
          {saving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
